import React, { useState } from 'react'
import { ChevronDown, Eye } from 'lucide-react'
import { IDepartureCarModel, IReturnedCarModel } from '../../app/pages/dashboard/DashboardModel'
import { Link } from 'react-router-dom'

export type TabKey = 'late' | 'today' | 'past' | 'tomorrow'

export interface TabHeader<T extends string = string> {
  id: T
  label: string
  count: number
}

type Props<T extends string, U extends IReturnedCarModel | IDepartureCarModel> = {
  name: 'in' | 'out'
  header: string
  tabs: TabHeader<T>[]
  activeTab: T
  setTab: (tab: T) => void
  data: U[]
  loading?: boolean
}

const styles = {
  container: {
    width: '100%',
    padding: '16px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    position: 'relative' as const, // overlay için
  },
  headerSection: { marginBottom: '16px' },
  title: { fontSize: '18px', fontWeight: 600, marginBottom: '16px' },
  tabContainer: { display: 'flex', alignItems: 'flex-end', borderBottom: '2px solid #d1d5db' },
  tabButton: (active: boolean, isIn: boolean) => ({
    padding: isIn ? '8px 10px' : '8px 6px',
    fontSize: '14px',
    backgroundColor: active ? '#ffffff' : '#f9fafb',
    border: active ? '2px solid #d1d5db' : 'none',
    borderBottom: active ? '2px solid #ffffff' : '2px solid #d1d5db',
    marginBottom: active ? '-2px' : 0,
    color: active ? '#111827' : '#6b7280',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '4px',
  }),
  tabCount: (active: boolean) => ({
    fontSize: '12px',
    fontWeight: 600,
    color: active ? '#111827' : '#9ca3af',
  }),
  dropdownContainer: { position: 'relative' as const },
  dropdownButton: (active: boolean) => ({
    padding: '8px 14px',
    backgroundColor: active ? '#ffffff' : '#f9fafb',
    border: active ? '2px solid #d1d5db' : 'none',
    borderBottom: active ? '2px solid #ffffff' : '2px solid #d1d5db',
    marginBottom: active ? '-2px' : 0,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '4px',
  }),
  dropdownMenu: {
    position: 'absolute' as const,
    left: 0,
    top: '100%',
    marginTop: '4px',
    backgroundColor: '#ffffff',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    minWidth: '200px',
    zIndex: 10,
  },
  dropdownItem: (active: boolean) => ({
    width: '100%',
    padding: '8px 12px',
    backgroundColor: active ? '#eff6ff' : 'transparent',
    color: active ? '#2563eb' : '#374151',
    cursor: 'pointer',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  }),
  table: { width: '100%', marginTop: '16px', borderCollapse: 'collapse' as const },
  th: { padding: '12px', backgroundColor: '#f3f4f6', fontSize: '13px', fontWeight: 600, textAlign: 'left' as const },
  td: { padding: '12px', fontSize: '13px' },
  overlay: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(255,255,255,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 20,
    fontSize: '16px',
    fontWeight: 600,
    color: '#111827',
  },
}

const VehicleTrackingSystem = <T extends string, U extends IReturnedCarModel | IDepartureCarModel>({
  header,
  tabs,
  activeTab,
  setTab,
  data,
  name,
  loading = false,
}: Props<T, U>) => {
  const [open, setOpen] = useState(false)

  const visibleTabs = tabs.slice(0, 3)
  const dropdownTabs = tabs.slice(3)

  return (
    <div style={styles.container}>
      {loading && <div style={styles.overlay}>Yükleniyor...</div>}

      <div style={styles.headerSection}>
        <h2 style={styles.title}>{header}</h2>

        <div style={styles.tabContainer}>
          {visibleTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setTab(tab.id)}
              style={styles.tabButton(activeTab === tab.id, name === 'in')}
            >
              <span style={styles.tabCount(activeTab === tab.id)}>{tab.count}</span>
              <span>{tab.label}</span>
            </button>
          ))}

          {dropdownTabs.length > 0 && (
            <div style={styles.dropdownContainer}>
              <button
                onClick={() => setOpen(p => !p)}
                style={styles.dropdownButton(dropdownTabs.some(t => t.id === activeTab))}
              >
                <span style={styles.tabCount(false)}>
                  {dropdownTabs.reduce((s, t) => s + t.count, 0)}
                </span>
                <ChevronDown size={16} />
              </button>

              {open && (
                <div style={styles.dropdownMenu}>
                  {dropdownTabs.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setTab(tab.id)
                        setOpen(false)
                      }}
                      style={styles.dropdownItem(activeTab === tab.id)}
                    >
                      <strong>{tab.count}</strong>
                      {tab.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Gör</th>
            <th style={styles.th}>Tarih / Saat</th>
            <th style={styles.th}>Ad Soyad</th>
            <th style={styles.th}>Plaka</th>
            <th style={styles.th}>Şube</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 && !loading && (
            <tr>
              <td colSpan={5} style={styles.td}>
                Kayıt bulunamadı
              </td>
            </tr>
          )}

          {data.map(row => (
            <tr key={row.id}>
              <td style={styles.td}>
                {name === 'out' ? (
                  <Link to={`/reservation?reservationId=${row.id}`} style={{color: '#2563eb', display: 'flex', alignItems: 'center', gap: '4px'}}>
                    <Eye size={18} />
                  </Link>
                ) : (
                  <Link to={`/rent?rentId=${row.id}`} style={{color: '#2563eb', display: 'flex', alignItems: 'center', gap: '4px'}}>
                    <Eye size={18} />
                  </Link>
                )}
              </td>
               
              <td style={styles.td}>
                {name === 'out'
                  ? `${(row as IDepartureCarModel).startDate} ${(row as IDepartureCarModel).startTime}`
                  : `${(row as IReturnedCarModel).endDate} ${(row as IReturnedCarModel).endTime}`}
              </td>
              <td style={styles.td}>{row.customerFullName}</td>
              <td style={styles.td}>{row.carPlate}</td>
              <td style={styles.td}>
                {name === 'out'
                  ? (row as IDepartureCarModel).departureBranchName
                  : (row as IReturnedCarModel).returnBranchName}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default VehicleTrackingSystem
